import React, { useState } from 'react';
import setUiMessage from '../../../Utilities/Tostify';
import { post } from '../../Auths/AuthToken';
import ReactDatePicker from 'react-datepicker';
import { DEFAULT_DATE_FORMATE } from '../../../constents';
import { ssnInputMask } from '../../../Utilities/MaskInput';
import { format } from 'date-fns';
import Button from '../../Button';
import Select from 'react-select';

const buildOptions = (arr, labelKey) =>
  (arr || []).map((item) => ({ value: item?.id, label: item?.[labelKey] }));

const getSelectedOption = (options, value) =>
  (options || []).find((o) => String(o.value) === String(value)) || null;

const EMPTY_PARTY = {
  ctm: '',
  guardian_email_id: '',
  guardian_first_name: '',
  guardian_last_name: '',
  guardian_middle_name: '',
  informant: '',
  is_finance_res_party: '0',
  is_self: '',
  res_party_address1: '',
  res_party_address2: '',
  res_party_city: '',
  res_party_country: '',
  res_party_dob: '',
  res_party_external_id: '',
  res_party_gender: '',
  res_party_home_phone: '',
  res_party_mobile: '',
  res_party_relationship: '',
  res_party_ssn: '',
  res_party_state: '',
  res_party_work_phone: '',
  res_party_zip: '',
  responsible_party_id: '',
  addToMaster: '',
  party_status: 'new',
};

const randomExternalId = () =>
  Math.random().toString(36).slice(2, 8).toUpperCase();

function ResponsibleParty({ data, patientId, reload }) {
  const [isEdit, setIsEdit] = useState(false);
  const [allDataEdit, setAllDataEdit] = useState(null);
  const [formValues, setFormValues] = useState([{ ...EMPTY_PARTY }]);

  const getResPartyData = async (id, i) => {
    try {
      const response = await post('api/patient/Patient/resposibalPartyData', {
        responsible_party_id: id,
        patient_id: patientId,
      });
      if (response?.status === 200) {
        const sourceData = response?.data;
        setFormValues((ele) => {
          const sel = [...ele];
          const financeFlag =
            sourceData.is_finance_res_party === '1' ||
            sourceData.is_finance_res_party === 1
              ? '1'
              : '0';
          sel[i] = {
            ...ele[i],
            ctm: sourceData.ctm,
            guardian_email_id: sourceData.email_id,
            guardian_first_name: sourceData.first_name,
            guardian_last_name: sourceData.last_name,
            guardian_middle_name: sourceData.middle_name,
            informant: sourceData.informant,
            is_finance_res_party: financeFlag,
            is_self: sourceData.is_self,
            res_party_address1: sourceData.address_1,
            res_party_address2: sourceData.address_2,
            res_party_city: sourceData.city,
            res_party_country: sourceData.country,
            res_party_dob: sourceData.date_of_birth,
            res_party_external_id: sourceData.external_id,
            res_party_gender: sourceData.gender
              ? String(sourceData.gender).toLowerCase()
              : '',
            res_party_home_phone: sourceData.home_phone,
            res_party_mobile: sourceData.mobile,
            res_party_relationship:
              id === 'Self' ? '' : sourceData.relationship,
            res_party_ssn: sourceData.ssn,
            res_party_state: sourceData.state,
            res_party_work_phone: sourceData.work_phone,
            res_party_zip: sourceData.zip,
            // Self has no master id — keep "Self" so Sex/save logic stays correct
            responsible_party_id: id === 'Self' ? 'Self' : sourceData.id || id,
          };
          // Only one financial responsible party allowed
          if (financeFlag === '1') {
            return sel.map((row, idx) =>
              idx === i ? row : { ...row, is_finance_res_party: '0' }
            );
          }
          return sel;
        });
      }
    } catch (e) {
      setUiMessage('err', e.message);
    }
  };

  const handleResPartyChange = async () => {
    setIsEdit(true);
    try {
      const response = await post('api/patient/Patient/editPatientProfile', {
        patientId: patientId,
      });
      if (response?.status === 200) {
        setAllDataEdit(response?.data);
        const patientDetails = response?.data?.patientResponsibleData;
        if (patientDetails?.length) {
          let financeAssigned = false;
          setFormValues(
            patientDetails.map((row) => {
              const isFinance =
                !financeAssigned &&
                (row?.is_finance_res_party === '1' ||
                  row?.is_finance_res_party === 1);
              if (isFinance) financeAssigned = true;
              return {
                ...EMPTY_PARTY,
                ...row,
                is_finance_res_party: isFinance ? '1' : '0',
                party_status:
                  row?.party_status || (row?.id ? `old_${row.id}` : 'new'),
              };
            })
          );
        } else {
          setFormValues([{ ...EMPTY_PARTY, party_status: 'new' }]);
        }
      }
    } catch (e) {
      setUiMessage('err', e.message);
    }
  };

  const addMoreResponsibleParty = () => {
    setFormValues((prev) => [
      ...prev,
      {
        ...EMPTY_PARTY,
        party_status: 'new',
        res_party_external_id: randomExternalId(),
      },
    ]);
  };

  const removeResponsibleParty = (index) => {
    setFormValues((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearResponsibleParty = (index) => {
    setFormValues((prev) => {
      const sel = [...prev];
      const existing = sel[index];
      sel[index] = {
        ...EMPTY_PARTY,
        id: existing?.id,
        party_status:
          existing?.party_status ||
          (existing?.id ? `old_${existing.id}` : 'new'),
        res_party_external_id: randomExternalId(),
      };
      return sel;
    });
  };

  const handleSelectChange = (id, option, i) => {
    const value = option ? option.value : '';
    if (id === 'responsible_party_id') {
      getResPartyData(value, i);
    } else if (id === 'is_finance_res_party') {
      const isYes = value === '1' || value === 1;
      if (isYes) {
        const alreadySet = formValues.some(
          (row, idx) =>
            idx !== i &&
            (row?.is_finance_res_party === '1' ||
              row?.is_finance_res_party === 1)
        );
        if (alreadySet) {
          setUiMessage(
            'inf',
            'Please select only one financial responsible party at a time!'
          );
        }
      }
      setFormValues((ele) =>
        ele.map((row, idx) => ({
          ...row,
          is_finance_res_party:
            idx === i ? (isYes ? '1' : '0') : isYes ? '0' : row.is_finance_res_party,
        }))
      );
      return;
    }
    setFormValues((ele) => {
      const sel = [...ele];
      sel[i] = { ...ele[i], [id]: value };
      return sel;
    });
  };

  const handleFieldChange = ({ target: { id, value } }, i) => {
    if (id === 'res_party_ssn') {
      setFormValues((ele) => {
        const sel = [...ele];
        sel[i] = { ...ele[i], res_party_ssn: ssnInputMask(value) };
        return sel;
      });
    } else if (id === 'responsible_party_id') {
      getResPartyData(value, i);
      setFormValues((ele) => {
        const sel = [...ele];
        sel[i] = { ...ele[i], [id]: value };
        return sel;
      });
    } else if (id === 'is_finance_res_party') {
      const isYes = value === '1' || value === 1;
      if (isYes) {
        const alreadySet = formValues.some(
          (row, idx) =>
            idx !== i &&
            (row?.is_finance_res_party === '1' ||
              row?.is_finance_res_party === 1)
        );
        if (alreadySet) {
          setUiMessage(
            'inf',
            'Please select only one financial responsible party at a time!'
          );
        }
      }
      setFormValues((ele) =>
        ele.map((row, idx) => ({
          ...row,
          is_finance_res_party:
            idx === i ? (isYes ? '1' : '0') : isYes ? '0' : row.is_finance_res_party,
        }))
      );
    } else {
      setFormValues((ele) => {
        const sel = [...ele];
        sel[i] = { ...ele[i], [id]: value };
        return sel;
      });
    }
  };

  const setPatDob = (dob, i) => {
    setFormValues((ele) => {
      const sel = [...ele];
      sel[i] = { ...ele[i], res_party_dob: dob };
      return sel;
    });
  };

  const formatResPartyDob = (dob) => {
    if (!dob) return "";
    if (dob instanceof Date) {
      return isNaN(dob.getTime()) ? "" : format(dob, DEFAULT_DATE_FORMATE);
    }
    const parsed = new Date(dob);
    return isNaN(parsed.getTime()) ? dob : format(parsed, DEFAULT_DATE_FORMATE);
  };

  const handleSave = async () => {
    setIsEdit(false);

    // Enforce single financial responsible party in payload
    let financeAssigned = false;
    const normalizedParties = formValues.map((obj) => {
      const isFinance =
        !financeAssigned &&
        (obj?.is_finance_res_party === '1' || obj?.is_finance_res_party === 1);
      if (isFinance) financeAssigned = true;
      return { ...obj, is_finance_res_party: isFinance ? '1' : '0' };
    });

    try {
      const response = await post('api/patient/Patient/updatePatient', {
        updateInfo: 'Guardian-Information',
        patient_id: patientId,
        addToMaster: normalizedParties.map((obj) => 0).join(','),
        allCtm: normalizedParties.map((obj) => obj?.ctm).join(','),
        allInformant: normalizedParties.map((obj) => obj?.informant).join(','),
        isFinanceParty: normalizedParties
          .map((obj) => obj?.is_finance_res_party)
          .join(','),
        party_status: normalizedParties.map((obj) =>
          obj?.party_status || (obj?.id ? "old_" + obj?.id : "new"),
        ),
        responsible_party_id: normalizedParties.map((obj) =>
          parseInt(obj?.responsible_party_id) === 0 ||
          obj?.responsible_party_id === "Self"
            ? "Self"
            : obj?.responsible_party_id,
        ),
        guardian_first_name: normalizedParties.map((obj) => obj?.guardian_first_name),
        guardian_middle_name: normalizedParties.map(
          (obj) => obj?.guardian_middle_name
        ),
        guardian_last_name: normalizedParties.map((obj) => obj?.guardian_last_name),
        res_party_dob: normalizedParties.map((obj) => formatResPartyDob(obj?.res_party_dob)),
        res_party_gender: normalizedParties.map((obj) => obj?.res_party_gender),
        guardian_email_id: normalizedParties.map((obj) => obj?.guardian_email_id),
        res_party_ssn: normalizedParties.map((obj) => obj?.res_party_ssn),
        res_party_relationship: normalizedParties.map(
          (obj) => obj?.res_party_relationship
        ),
        addUpdateIntoMaster: normalizedParties.map((obj) => 0),
        is_finance_res_party: normalizedParties.map(
          (obj) => obj?.is_finance_res_party
        ),
        res_party_external_id: normalizedParties.map(
          (obj) => obj?.res_party_external_id
        ),
        res_party_home_phone: normalizedParties.map(
          (obj) => obj?.res_party_home_phone
        ),
        res_party_work_phone: normalizedParties.map(
          (obj) => obj?.res_party_work_phone
        ),
        res_party_mobile: normalizedParties.map((obj) => obj?.res_party_mobile),
        res_party_address1: normalizedParties.map((obj) => obj?.res_party_address1),
        res_party_address2: normalizedParties.map((obj) => obj?.res_party_address2),
        res_party_zip: normalizedParties.map((obj) => obj?.res_party_zip),
        res_party_city: normalizedParties.map((obj) => obj?.res_party_city),
        res_party_state: normalizedParties.map((obj) => obj?.res_party_state),
        res_party_country: normalizedParties.map((obj) => obj?.res_party_country),
        loginUserId: '2',
      });
      if (response?.status === 200) {
        reload();
      }
    } catch (e) {
      setUiMessage('err', e.message);
    }
  };
  return (
    <>
      {isEdit ? (
        <div className="demo-main-con-sec">
          <div
            className="demo-main-con-chld-hdr"
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div
              className="chld-hdr-hdng"
              style={{
                width: 'auto',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                paddingRight: '8px',
              }}
            >
              <span>Responsible Party:</span>
              <Button
                variant="outline"
                onClick={addMoreResponsibleParty}
                style={{ fontSize: '13px', padding: '3px 12px', flexShrink: 0 }}
              >
                Add More
              </Button>
            </div>
            <div
              className="chld-hdr-edt"
              style={{
                width: 'auto',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="default"
                onClick={() => setIsEdit(false)}
                style={{ fontSize: '13px', padding: '3px 12px' }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                style={{ fontSize: '13px', padding: '3px 12px' }}
              >
                Save
              </Button>
            </div>
          </div>
          {formValues &&
            formValues.map((item, i) => {
              return (
                <div
                  key={i}
                  className="demo-main-con-chld-bdy"
                  style={{
                    marginBottom: '10px',
                    borderTop: i > 0 ? '1px dotted #ccc' : undefined,
                    paddingTop: i > 0 ? '8px' : undefined,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: '4px 15px 0',
                    }}
                  >
                    {formValues.length > 1 ? (
                      <Button
                        variant="danger"
                        onClick={() => removeResponsibleParty(i)}
                        style={{ fontSize: '12px', padding: '2px 10px' }}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() => clearResponsibleParty(i)}
                        style={{ fontSize: '12px', padding: '2px 10px' }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Responsible Party:</span>
                      <span className="nsu-act-val">
                        {(() => {
                          const resPartyOptions = [
                            { value: 'Self', label: 'Self' },
                            ...buildOptions(
                              allDataEdit?.respPartyList,
                              'full_name'
                            ),
                          ];
                          const selectedValue =
                            parseInt(item?.responsible_party_id) === 0 ||
                            item?.responsible_party_id === 'Self' ||
                            !item?.responsible_party_id
                              ? 'Self'
                              : item?.responsible_party_id;
                          return (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isClearable={false}
                              placeholder="Select"
                              value={getSelectedOption(
                                resPartyOptions,
                                selectedValue
                              )}
                              options={resPartyOptions}
                              onChange={(opt) =>
                                handleSelectChange(
                                  'responsible_party_id',
                                  opt,
                                  i
                                )
                              }
                            />
                          );
                        })()}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">SSN:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_ssn"
                          value={item?.res_party_ssn || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                          maxLength="11"
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Address1:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_address1"
                          value={item?.res_party_address1 || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">First Name:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="guardian_first_name"
                          value={item?.guardian_first_name || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Home Phone:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_home_phone"
                          value={item?.res_party_home_phone || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                          maxLength="10"
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Address2:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_address2"
                          value={item?.res_party_address2 || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Middle Name:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="guardian_middle_name"
                          value={item?.guardian_middle_name || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Mobile Phone:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_mobile"
                          value={item?.res_party_mobile || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                          maxLength="10"
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">City:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_city"
                          value={item?.res_party_city || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Last Name:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="guardian_last_name"
                          value={item?.guardian_last_name || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Work Phone:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_work_phone"
                          value={item?.res_party_work_phone || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                          maxLength="10"
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Zip:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="res_party_zip"
                          value={item?.res_party_zip || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                          maxLength="5"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">DOB:</span>
                      <span className="nsu-act-val">
                        <ReactDatePicker
                          selected={(() => {
                            if (
                              !item?.res_party_dob ||
                              item?.res_party_dob === "0000-00-00"
                            ) {
                              return null;
                            }
                            const d =
                              item.res_party_dob instanceof Date
                                ? item.res_party_dob
                                : new Date(item.res_party_dob);
                            return isNaN(d.getTime()) ? null : d;
                          })()}
                          onChange={(date) => setPatDob(date, i)}
                          className="inpt-width-nw"
                          dateFormat={DEFAULT_DATE_FORMATE}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Informant:</span>
                      <span className="nsu-act-val">
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={false}
                          placeholder="Select"
                          value={getSelectedOption(
                            [
                              { value: '0', label: 'No' },
                              { value: '1', label: 'Yes' },
                            ],
                            item.informant || '0'
                          )}
                          options={[
                            { value: '0', label: 'No' },
                            { value: '1', label: 'Yes' },
                          ]}
                          onChange={(opt) =>
                            handleSelectChange('informant', opt, i)
                          }
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">State:</span>
                      <span className="nsu-act-val">
                        {(() => {
                          const stateOptions = buildOptions(
                            allDataEdit?.contactDetailArr?.stateList,
                            'iso_code'
                          );
                          return (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isClearable
                              placeholder="Select"
                              value={getSelectedOption(
                                stateOptions,
                                item?.res_party_state
                              )}
                              options={stateOptions}
                              onChange={(opt) =>
                                handleSelectChange('res_party_state', opt, i)
                              }
                            />
                          );
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Sex:</span>
                      <span className="nsu-act-val">
                        {(() => {
                          const genderOptions = buildOptions(
                            allDataEdit?.identityDetailArr?.sex,
                            'val'
                          );
                          return (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isClearable
                              placeholder="Select"
                              value={getSelectedOption(
                                genderOptions,
                                item?.res_party_gender
                              )}
                              options={genderOptions}
                              onChange={(opt) =>
                                handleSelectChange('res_party_gender', opt, i)
                              }
                            />
                          );
                        })()}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Care of team member:</span>
                      <span className="nsu-act-val">
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={false}
                          placeholder="Select"
                          value={getSelectedOption(
                            [
                              { value: '0', label: 'No' },
                              { value: '1', label: 'Yes' },
                            ],
                            item?.ctm || '0'
                          )}
                          options={[
                            { value: '0', label: 'No' },
                            { value: '1', label: 'Yes' },
                          ]}
                          onChange={(opt) =>
                            handleSelectChange('ctm', opt, i)
                          }
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Country:</span>
                      <span className="nsu-act-val">
                        {(() => {
                          const countryOptions = buildOptions(
                            allDataEdit?.contactDetailArr?.countryList,
                            'country_name'
                          );
                          return (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isClearable
                              placeholder="Select"
                              value={getSelectedOption(
                                countryOptions,
                                item?.res_party_country
                              )}
                              options={countryOptions}
                              onChange={(opt) =>
                                handleSelectChange('res_party_country', opt, i)
                              }
                            />
                          );
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Email ID:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder=""
                          id="guardian_email_id"
                          value={item?.guardian_email_id || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">External ID:</span>
                      <span className="nsu-act-val">
                        <input
                          type="text"
                          className="inpt-width-nw"
                          placeholder="Write external ID here.."
                          id="res_party_external_id"
                          value={item?.res_party_external_id || ''}
                          onChange={(e) => handleFieldChange(e, i)}
                        />
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">
                        Financial Responsible Party:
                      </span>
                      <span className="nsu-act-val">
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={false}
                          placeholder="Select"
                          value={getSelectedOption(
                            [
                              { value: '0', label: 'No' },
                              { value: '1', label: 'Yes' },
                            ],
                            item?.is_finance_res_party || '0'
                          )}
                          options={[
                            { value: '0', label: 'No' },
                            { value: '1', label: 'Yes' },
                          ]}
                          onChange={(opt) =>
                            handleSelectChange('is_finance_res_party', opt, i)
                          }
                        />
                      </span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Relationship:</span>
                      <span className="nsu-act-val">
                        {(() => {
                          const relationshipOptions = buildOptions(
                            allDataEdit?.emergencyContactDetailArr
                              ?.relationship,
                            'title'
                          );
                          return (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isClearable
                              placeholder="Select"
                              value={getSelectedOption(
                                relationshipOptions,
                                item?.res_party_relationship
                              )}
                              options={relationshipOptions}
                              onChange={(opt) =>
                                handleSelectChange(
                                  'res_party_relationship',
                                  opt,
                                  i
                                )
                              }
                            />
                          );
                        })()}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">{''}</span>
                      <span className="nsu-act-val">{''}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">{''}</span>
                      <span className="nsu-act-val">{''}</span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div className="demo-main-con-sec">
          <div className="demo-main-con-chld-hdr">
            <div className="chld-hdr-hdng">Responsible Party:</div>
            <div className="chld-hdr-edt">
              <span className="demo-edt-icn" onClick={handleResPartyChange}>
                &#9998;
              </span>
            </div>
          </div>
          {data &&
            data.map((item, i) => {
              return (
                <div
                  key={i}
                  className="demo-main-con-chld-bdy"
                  style={{ marginTop: '10px' }}
                >
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Responsible Party:</span>
                      <span className="nsu-act-val">
                        {item?.responsiblePartyName
                          ? item?.responsiblePartyName
                          : 'self'}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">SSN:</span>
                      <span className="nsu-act-val">{item?.ssn}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Address1:</span>
                      <span className="nsu-act-val">{item?.address1}</span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">First Name:</span>
                      <span className="nsu-act-val">
                        {item?.guardian_f_name}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Home Phone:</span>
                      <span className="nsu-act-val">{item?.home_phone}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Address2:</span>
                      <span className="nsu-act-val">{item?.address2}</span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Middle Name:</span>
                      <span className="nsu-act-val">
                        {item?.guardian_m_name}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Mobile Phone:</span>
                      <span className="nsu-act-val">{item?.mobile}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">City:</span>
                      <span className="nsu-act-val">{item?.city}</span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Last Name:</span>
                      <span className="nsu-act-val">
                        {item?.guardian_l_name}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Work Phone:</span>
                      <span className="nsu-act-val">{item?.work_phone}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Zip:</span>
                      <span className="nsu-act-val">{item?.zip}</span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">DOB:</span>
                      <span className="nsu-act-val">{item?.dob}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Informant:</span>
                      <span className="nsu-act-val">{item?.informent}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">State:</span>
                      <span className="nsu-act-val">{item?.state}</span>
                    </div>
                  </div>
                  <div className="flex-sec">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Sex:</span>
                      <span className="nsu-act-val">{item?.sex}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Care of team member:</span>
                      <span className="nsu-act-val">
                        {item?.careOfTeamMember}
                      </span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Country:</span>
                      <span className="nsu-act-val">{item?.country}</span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Email ID:</span>
                      <span className="nsu-act-val">{item?.email}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">External ID:</span>
                      <span className="nsu-act-val">{item?.external_id}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">
                        Financial Responsible Party:
                      </span>
                      <span className="nsu-act-val">{item?.finResParty}</span>
                    </div>
                  </div>
                  <div className="flex-sec alt-rw">
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl">Responsible Party:</span>
                      <span className="nsu-act-val">{item?.relationship}</span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl"></span>
                      <span className="nsu-act-val"></span>
                    </div>
                    <div className="demo-main-con-chld-bdy-rw">
                      <span className="nsu-act-lbl"></span>
                      <span className="nsu-act-val"></span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}

export default ResponsibleParty;
